import type { LogicalAxis, ResolvedChannels } from './channels';
import type { CalibrationFrame, CalibrationStep, CalibrationWizard } from './calibration';
import type { StoredInputProfile } from './profiles';

export interface CalibrationScreenOptions {
  document?: Document;
  onSave?: (profile: StoredInputProfile) => void;
  onTestFlight?: (channels: ResolvedChannels) => void;
}

interface AxisNodes {
  label: HTMLElement;
  raw: HTMLElement;
  calibrated: HTMLElement;
}

const CHANNELS: readonly LogicalAxis[] = ['roll', 'pitch', 'yaw', 'throttle'];

const STEP_HELP: Record<CalibrationStep, string> = {
  'detect-center':
    'Leave every stick, switch, and dial untouched so the wizard can record each axis at rest.',
  'sweep-extremes': 'Move every stick, switch, and dial through its full range in both directions.',
  'assign-channels':
    'Click "Auto-assign channels", or move roll, pitch, yaw, and throttle one at a time so the wizard can tell them apart.',
  'tune-curves': 'Adjust deadzone, expo, and reverse for each channel below.',
  'assign-aux':
    'Arm / mode toggle / reset / release-manual still use the default button layout; click Next to continue.',
  review: 'Check the summary below, then Save.',
};

const addClass = <T extends HTMLElement>(element: T, ...names: string[]): T => {
  element.classList.add(...names);
  return element;
};

const clamp = (value: number): number => Math.min(1, Math.max(-1, value));

/** Retained DOM shell for CalibrationWizard; all elements are allocated once. */
export class CalibrationScreen {
  readonly root: HTMLElement;

  private readonly step: HTMLElement;
  private readonly instruction: HTMLElement;
  private readonly axisNodes: AxisNodes[];
  private readonly axesSection: HTMLElement;
  private readonly stickSection: HTMLElement;
  private readonly stick: HTMLElement;
  private readonly curveEditor: HTMLElement;
  private readonly autoAssign: HTMLButtonElement;
  private readonly save: HTMLButtonElement;
  private readonly blockedReason: HTMLElement;
  private lastFrame: CalibrationFrame = { axes: [], buttons: [] };

  constructor(
    private readonly wizard: CalibrationWizard,
    options: CalibrationScreenOptions = {},
  ) {
    const dom = options.document ?? (typeof document === 'undefined' ? undefined : document);
    if (dom === undefined) {
      throw new Error(
        'CalibrationScreen requires a DOM document; inject one when running outside a browser',
      );
    }

    this.root = addClass(dom.createElement('section'), 'calibration-screen');
    this.step = addClass(dom.createElement('h2'), 'calibration-step');
    this.root.appendChild(this.step);
    this.instruction = addClass(dom.createElement('p'), 'calibration-instruction');
    this.root.appendChild(this.instruction);

    const axes = addClass(dom.createElement('div'), 'calibration-axes');
    this.axesSection = axes;
    this.axisNodes = wizard.screenState().perAxis.map(({ index }) => {
      const row = addClass(dom.createElement('div'), 'calibration-axis');
      const label = addClass(dom.createElement('span'), 'calibration-axis-label');
      const track = addClass(dom.createElement('div'), 'calibration-axis-track');
      const raw = addClass(dom.createElement('div'), 'calibration-axis-raw');
      const calibrated = addClass(dom.createElement('div'), 'calibration-axis-calibrated');
      label.textContent = `Axis ${index}`;
      track.appendChild(raw);
      track.appendChild(calibrated);
      row.appendChild(label);
      row.appendChild(track);
      axes.appendChild(row);
      return { label, raw, calibrated };
    });
    this.root.appendChild(axes);

    const stickTrack = addClass(dom.createElement('div'), 'calibration-stick-track');
    this.stickSection = stickTrack;
    this.stick = addClass(dom.createElement('div'), 'calibration-stick');
    stickTrack.appendChild(this.stick);
    this.root.appendChild(stickTrack);

    const curveEditor = addClass(dom.createElement('section'), 'calibration-curve-editor');
    this.curveEditor = curveEditor;
    CHANNELS.forEach((channel) => {
      const row = addClass(dom.createElement('label'), 'calibration-curve-row');
      const name = addClass(dom.createElement('span'), 'calibration-curve-name');
      name.textContent = channel;
      const deadzone = addClass(
        dom.createElement('input'),
        'calibration-deadzone',
        `calibration-deadzone-${channel}`,
      );
      deadzone.type = 'range';
      deadzone.min = '0';
      deadzone.max = '1';
      deadzone.step = '0.01';
      deadzone.value = '0';
      deadzone.addEventListener('input', () => {
        this.wizard.setCurve(channel, { deadzonePct: Number(deadzone.value) });
      });
      const expo = addClass(
        dom.createElement('input'),
        'calibration-expo',
        `calibration-expo-${channel}`,
      );
      expo.type = 'range';
      expo.min = '0';
      expo.max = '1';
      expo.step = '0.01';
      expo.value = '0';
      expo.addEventListener('input', () => {
        this.wizard.setCurve(channel, { expo: Number(expo.value) });
      });
      const reverse = addClass(
        dom.createElement('input'),
        'calibration-reverse',
        `calibration-reverse-${channel}`,
      );
      reverse.type = 'checkbox';
      reverse.checked = false;
      reverse.addEventListener('change', () => {
        this.wizard.setCurve(channel, { reverse: reverse.checked });
      });
      row.appendChild(name);
      row.appendChild(deadzone);
      row.appendChild(expo);
      row.appendChild(reverse);
      curveEditor.appendChild(row);
    });
    this.root.appendChild(curveEditor);

    const actions = addClass(dom.createElement('div'), 'calibration-actions');
    const back = addClass(dom.createElement('button'), 'calibration-back');
    back.textContent = 'Back';
    back.addEventListener('click', () => {
      this.wizard.back();
      this.render(this.lastFrame);
    });
    const next = addClass(dom.createElement('button'), 'calibration-next');
    next.textContent = 'Next';
    next.addEventListener('click', () => {
      this.wizard.advance();
      this.render(this.lastFrame);
    });
    const autoAssign = addClass(dom.createElement('button'), 'calibration-auto-assign');
    this.autoAssign = autoAssign;
    autoAssign.textContent = 'Auto-assign channels';
    autoAssign.addEventListener('click', () => {
      this.wizard.autoAssignChannels();
      this.render(this.lastFrame);
    });
    const testFlight = addClass(dom.createElement('button'), 'calibration-test-flight');
    testFlight.textContent = 'Test flight';
    testFlight.addEventListener('click', () => {
      options.onTestFlight?.(this.wizard.screenState().previewAt(this.lastFrame.axes));
    });
    this.save = addClass(dom.createElement('button'), 'calibration-save');
    this.save.textContent = 'Save';
    this.save.addEventListener('click', () => {
      if (!this.wizard.screenState().canSave) return;
      options.onSave?.(this.wizard.buildStoredProfile());
    });
    actions.appendChild(back);
    actions.appendChild(next);
    actions.appendChild(autoAssign);
    actions.appendChild(testFlight);
    actions.appendChild(this.save);
    this.root.appendChild(actions);

    this.blockedReason = addClass(dom.createElement('p'), 'calibration-blocked-reason');
    this.root.appendChild(this.blockedReason);
    this.render(this.lastFrame);
  }

  render(rawFrame: CalibrationFrame): void {
    this.lastFrame = { axes: Array.from(rawFrame.axes), buttons: Array.from(rawFrame.buttons) };
    const state = this.wizard.screenState();
    const resolved = state.previewAt(rawFrame.axes);
    this.step.textContent = state.step;
    this.instruction.textContent = STEP_HELP[state.step];

    this.axesSection.hidden = state.step === 'assign-aux' || state.step === 'review';
    this.stickSection.hidden = this.axesSection.hidden;
    this.curveEditor.hidden = state.step !== 'tune-curves';
    this.autoAssign.hidden = state.step !== 'assign-channels';

    this.axisNodes.forEach((nodes, index) => {
      const capture = state.perAxis[index]!;
      const raw = clamp(rawFrame.axes[index] ?? capture.center);
      const assignedChannel = CHANNELS.find(
        (channel) => state.channelAssignments[channel] === index,
      );
      const calibrated = assignedChannel === undefined ? 0 : resolved[assignedChannel];
      const displayCalibrated = assignedChannel === 'throttle' ? calibrated * 2 - 1 : calibrated;
      nodes.label.textContent = `Axis ${index}: ${capture.min.toFixed(3)} / ${capture.center.toFixed(3)} / ${capture.max.toFixed(3)}`;
      nodes.raw.style.transform = `translateX(${raw * 50}px)`;
      nodes.calibrated.style.transform = `translateX(${clamp(displayCalibrated) * 50}px)`;
      nodes.calibrated.style.opacity = assignedChannel === undefined ? '0.25' : '1';
    });

    this.stick.style.transform = `translate(${resolved.roll * 40}px, ${-resolved.pitch * 40}px)`;
    this.save.disabled = !state.canSave;
    const shownReason = state.step === 'review' ? state.blockedReason : state.nextStepBlockedReason;
    this.blockedReason.textContent = shownReason ?? '';
    this.blockedReason.style.opacity = shownReason === undefined ? '0' : '1';
  }
}
